<template>
  <v-container>
    <v-row no-gutters>
      <v-col cols="12" sm="1">
        <router-link
          :to="{ name: 'EstimatedInsuranceRequired' }"
          class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
      </v-col>
      <v-col cols="12" sm="11">
        <h1 class="mb-2">Answer a few quick questions</h1>
      </v-col>
    </v-row>
    <v-row no-gutters class="mt-sm-6">
      <v-col cols="12" sm="3" offset-sm="1" class="form-blurb">
        <v-img
          :src="require(`@/assets/${whitelabel.images.insuranceQuestionsPage}`)"
          aspect-ratio="1"
          alt="insurance questions"
          class="insurance-img"
          :class="{ 'insurance-img-xs': $vuetify.breakpoint.xs }"
        />
      </v-col>

      <v-col cols="12" sm="6" offset-sm="1">
        <ValidationObserver ref="observer">
          <v-form class="insurance-questions-form" @submit.prevent="submit">
            <v-row no-gutters>
              <v-col cols="12" md="6">
                <h4>Basic income per month?</h4>
              </v-col>
              <v-col cols="12" md="4">
                <ValidationProvider
                  id="monthy_income"
                  v-slot="{ errors }"
                  name="monthy_income"
                  rules="required"
                >
                  <v-text-field
                    v-model="currencyMonthlyIncome"
                    :error-messages="errors"
                    prefix="R"
                    type="text"
                    pattern="[0-9\s]*"
                    @keypress="isNumber($event)"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col cols="12" md="6">
                <h4>Education level</h4>
              </v-col>
              <v-col cols="12" md="4">
                <ValidationProvider
                  id="education_level"
                  v-slot="{ errors }"
                  name="education_level"
                  rules="required"
                >
                  <v-select
                    v-model="educationLevel"
                    :items="educationLevels"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col cols="12" md="6">
                <h4>Are you a smoker?</h4>
              </v-col>
              <v-col cols="12" md="4">
                <ValidationProvider
                  id="smoker"
                  v-slot="{ errors }"
                  name="smoker"
                  rules="required"
                >
                  <v-radio-group v-model="smoker" row :error-messages="errors">
                    <v-radio
                      :value="true"
                      color="primary"
                      label="Yes"
                      :on-icon="icons.mdiSmoking"
                      :off-icon="icons.mdiSmoking"
                    />
                    <v-radio
                      :value="false"
                      color="primary"
                      label="No"
                      :on-icon="icons.mdiSmokingOff"
                      :off-icon="icons.mdiSmokingOff"
                    />
                  </v-radio-group>
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col cols="12" md="6">
                <h4>Is this replacing an existing policy?</h4>
              </v-col>
              <v-col cols="12" md="4">
                <ValidationProvider
                  id="replacement_policy"
                  v-slot="{ errors }"
                  name="replacement_policy"
                  rules="required"
                >
                  <v-radio-group
                    v-model="replacementPolicy"
                    row
                    :error-messages="errors"
                  >
                    <v-radio :value="true" color="primary" label="Yes" />
                    <v-radio :value="false" color="primary" label="No" />
                  </v-radio-group>
                </ValidationProvider>
              </v-col>
              <v-col v-if="replacementPolicy" cols="12">
                <p>
                  If this policy is to replace any other policy with another
                  assurer, we want you to ensure that you make an informed
                  decision. You should contact your Financial Advisor who will
                  assist you with proper advice and a detailed comparison as
                  there may be limitations on the appropriateness of the
                  replacement with this product. If you choose to continue you
                  do so after consideration of this disclaimer and you do so at
                  your own risk.
                </p>
              </v-col>
            </v-row>

            <v-row class="my-sm-8 text-center text-sm-left">
              <v-col cols="12">
                <v-btn
                  rounded
                  depressed
                  large
                  color="primary"
                  type="submit"
                  :loading="buttonLoading"
                  ><span>Next</span
                  ><v-icon small>{{ icons.mdiArrowRight }}</v-icon></v-btn
                >
              </v-col>
            </v-row>
          </v-form>
        </ValidationObserver>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import { educationLevels } from '../../constants/js/insurance';
  import {
    formatCurrency,
    parseCurrency,
    isNumberCheck,
  } from '../../constants/js/currency-input';

  export default {
    name: 'InsuranceQuestions',
    props: {
      testator: {
        required: true,
        type: Object,
      },
      buttonLoading: {
        default: false,
        type: Boolean,
      },
    },
    data() {
      return {
        smoker: null,
        educationLevel: null,
        monthlyIncome: null,
        replacementPolicy: null,
        educationLevels: educationLevels,
      };
    },
    computed: {
      currencyMonthlyIncome: {
        get: function () {
          return formatCurrency(this.monthlyIncome);
        },
        set: function (newValue) {
          this.monthlyIncome = parseCurrency(newValue);
        },
      },
    },
    created() {
      if (this.testator.insurance) {
        this.setExistingFields();
      }
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            const insuranceInputDetails = {
              testatorId: this.testatorId,
              smoker: this.smoker,
              educationLevel: this.educationLevel,
              monthlyIncome: parseInt(this.monthlyIncome),
              replacementPolicy: this.replacementPolicy,
            };
            const params = {
              insuranceInputDetails: insuranceInputDetails,
              redirectLocation: 'InsuranceQuote',
            };
            this.$emit('updateInsurance', params);
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      setExistingFields() {
        // using native JSON functions removes reactivity
        // so we can clone an object/variable without mutating the original source
        this.smoker = JSON.parse(
          JSON.stringify(this.testator.insurance.smoker)
        );
        this.educationLevel = JSON.parse(
          JSON.stringify(this.testator.insurance.educationLevel)
        );
        this.monthlyIncome = JSON.parse(
          JSON.stringify(this.testator.insurance.monthlyIncome)
        );
        this.replacementPolicy = JSON.parse(
          JSON.stringify(this.testator.insurance.replacementPolicy)
        );
      },
      isNumber(event) {
        isNumberCheck(event);
      },
    },
  };
</script>

<style lang="scss">
  .insurance-questions-form {
    .v-input {
      margin-top: 0;
      padding-top: 0;
    }

    h4 {
      margin-top: 5px;
    }
  }
</style>
